<script>
    import { SendRequest } from "./APIService";
    import { showModal } from "./Modal";
    import moment from "moment";

    // Must match backend C# CRMAiDraftStatus. -1 asks the backend for every status.
    const STATUS = [
        { value: -1, label: "All", color: "dark" },
        { value: 0, label: "Pending", color: "primary" },
        { value: 1, label: "NeedsHuman", color: "warning" },
        { value: 2, label: "Failed", color: "danger" },
        { value: 3, label: "AutoSent", color: "success" },
    ];
    const TICKET_STATUS_COLOR = {
        UnderReview: "primary",
        NeedMoreInfo: "warning",
        WaitForUserResponse: "info",
        Closed: "secondary",
    };

    // State
    let stats = { pending: 0, needsHuman: 0, failed: 0, autoSent: 0 };
    let drafts = [];
    let activeStatus = -1;
    let page = 1;
    const PAGE_SIZE = 20;

    $: total = stats.pending + stats.needsHuman + stats.failed + stats.autoSent;

    // ── API calls ──────────────────────────────────────────────────────────────

    function loadStats() {
        SendRequest("crmAiDraftStats", "GET", "", (data) => {
            stats = data;
        });
    }

    function loadDrafts(status, p = 1) {
        activeStatus = status;
        page = p;
        loadStats();
        SendRequest(
            "crmAiDrafts",
            "GET",
            `status=${status}&page=${p}&pageSize=${PAGE_SIZE}`,
            (data) => {
                drafts = data ?? [];
            },
        );
    }

    function deleteUnneeded() {
        const confirmAction = confirm(
            "Remove the AI drafts of closed and deleted tickets from database? " +
                "Drafts of open tickets are kept. This can not be undone.",
        );
        if (!confirmAction) return;

        SendRequest(
            "crmAiDeleteUnneededDrafts",
            "POST",
            "",
            (data) => {
                if (data?.stats) stats = data.stats;
                showModal(
                    "Done",
                    `Deleted ${data?.deletedCount ?? 0} AI drafts`,
                    "OK",
                );
                loadDrafts(activeStatus, 1);
            },
            (error) => showModal("Error", error, "OK"),
            undefined,
            {},
        );
    }

    // ── Helpers ────────────────────────────────────────────────────────────────

    function fmt(d) {
        return d ? moment(d).format("YYYY-MM-DD HH:mm") : "—";
    }

    function statusOf(label) {
        return (
            STATUS.find((s) => s.label === label) ?? {
                label: String(label),
                color: "secondary",
            }
        );
    }

    // ── Init ───────────────────────────────────────────────────────────────────
    loadDrafts(-1);
</script>

<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!--  Stats Bar                                                                 -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<div class="row g-3 mb-3">
    <div class="col">
        <div class="stat-card border-dark clickable" on:click={() => loadDrafts(-1)}>
            <span class="stat-num text-dark">{total}</span>
            <span class="stat-lbl">All drafts</span>
        </div>
    </div>
    <div class="col">
        <div class="stat-card border-primary clickable" on:click={() => loadDrafts(0)}>
            <span class="stat-num text-primary">{stats.pending}</span>
            <span class="stat-lbl">Pending</span>
        </div>
    </div>
    <div class="col">
        <div class="stat-card border-warning clickable" on:click={() => loadDrafts(1)}>
            <span class="stat-num text-warning">{stats.needsHuman}</span>
            <span class="stat-lbl">Needs Human</span>
        </div>
    </div>
    <div class="col">
        <div class="stat-card border-danger clickable" on:click={() => loadDrafts(2)}>
            <span class="stat-num text-danger">{stats.failed}</span>
            <span class="stat-lbl">Failed</span>
        </div>
    </div>
    <div class="col">
        <div class="stat-card border-success clickable" on:click={() => loadDrafts(3)}>
            <span class="stat-num text-success">{stats.autoSent}</span>
            <span class="stat-lbl">Auto Sent</span>
        </div>
    </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!--  Draft list                                                                -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<div class="card">
    <div class="card-header">
        <div class="d-flex flex-wrap gap-1 align-items-center">
            {#each STATUS as s}
                <button
                    class="btn btn-sm {activeStatus === s.value
                        ? 'btn-' + s.color
                        : 'btn-outline-' + s.color}"
                    style="font-size:0.72rem; padding:2px 7px"
                    on:click={() => loadDrafts(s.value)}
                >
                    {s.label}
                </button>
            {/each}
            <button
                class="btn btn-sm btn-outline-secondary ms-2"
                title="Refresh"
                on:click={() => loadDrafts(activeStatus, page)}>↻</button
            >
            <button
                class="btn btn-sm btn-danger ms-auto"
                style="font-size:0.72rem; padding:2px 7px"
                title="Delete the drafts marked 'not needed'"
                on:click={deleteUnneeded}
            >
                🗑 Delete unneeded drafts
            </button>
        </div>
        <div class="hint mt-2">
            A draft is <b>not needed</b> when its ticket is closed or was deleted.
            Only those are deleted. Drafts of open tickets are kept: they stop the
            AI from answering the same message twice.
        </div>
    </div>

    <div class="table-responsive">
        <table class="table table-sm table-hover align-middle mb-0 draft-table">
            <thead>
                <tr>
                    <th>Created</th>
                    <th>Ticket</th>
                    <th>Draft</th>
                    <th>Text the AI wrote</th>
                </tr>
            </thead>
            <tbody>
                {#if drafts.length === 0}
                    <tr>
                        <td colspan="4" class="text-center text-muted py-5">
                            No drafts found
                        </td>
                    </tr>
                {:else}
                    {#each drafts as d (d.id)}
                        <tr>
                            <td class="meta nowrap">{fmt(d.createdAt)}</td>
                            <td>
                                <div class="ticket-title" dir="auto">
                                    {d.ticketTitle ?? "—"}
                                </div>
                                <div class="meta">
                                    #{d.ticketId?.slice(-8)}
                                    {#if d.ticketStatus}
                                        <span
                                            class="badge bg-{TICKET_STATUS_COLOR[
                                                d.ticketStatus
                                            ] ?? 'secondary'}">{d.ticketStatus}</span
                                        >
                                    {:else}
                                        <span class="badge bg-dark">ticket deleted</span>
                                    {/if}
                                </div>
                            </td>
                            <td>
                                <span class="badge bg-{statusOf(d.status).color}"
                                    >{d.status}</span
                                >
                                {#if d.autoReplies > 0}
                                    <span class="badge bg-success"
                                        >{d.autoReplies} sent</span
                                    >
                                {/if}
                                {#if d.askedForHuman}
                                    <span class="badge bg-warning text-dark"
                                        >AI asked for a human</span
                                    >
                                {/if}
                                {#if !d.needed}
                                    <span class="badge bg-secondary">not needed</span>
                                {/if}
                            </td>
                            <td>
                                <div class="draft-text" dir="auto">{d.draft ?? "—"}</div>
                                {#if d.reason}
                                    <div class="meta" dir="auto">{d.reason}</div>
                                {/if}
                            </td>
                        </tr>
                    {/each}
                {/if}
            </tbody>
        </table>
    </div>

    <!-- Pagination -->
    <div
        class="card-footer d-flex justify-content-between align-items-center py-1 px-2"
    >
        <button
            class="btn btn-sm btn-outline-secondary"
            disabled={page <= 1}
            on:click={() => loadDrafts(activeStatus, page - 1)}
        >
            ← Prev
        </button>
        <span class="text-muted" style="font-size:0.8rem">Page {page}</span>
        <button
            class="btn btn-sm btn-outline-secondary"
            disabled={drafts.length < PAGE_SIZE}
            on:click={() => loadDrafts(activeStatus, page + 1)}
        >
            Next →
        </button>
    </div>
</div>

<style>
    /* Stat bar cards */
    .stat-card {
        background: #fff;
        border: 2px solid;
        border-radius: 0.5rem;
        padding: 0.75rem 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.2rem;
    }
    .stat-num {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1;
    }
    .stat-lbl {
        font-size: 0.8rem;
        color: #6c757d;
    }

    /* Clickable hover */
    .clickable {
        cursor: pointer;
        transition: box-shadow 0.15s;
    }
    .clickable:hover {
        box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
    }

    .hint {
        font-size: 0.75rem;
        color: #6c757d;
    }

    /* Draft rows */
    .draft-table th {
        font-size: 0.75rem;
        color: #6c757d;
        font-weight: 600;
    }
    .draft-table td {
        padding: 0.5rem 0.75rem;
    }
    .draft-table .badge {
        font-size: 0.65rem;
    }
    .ticket-title {
        font-size: 0.83rem;
        font-weight: 600;
        line-height: 1.3;
    }
    .draft-text {
        font-size: 0.85rem;
        line-height: 1.4;
        white-space: pre-wrap;
    }
    .meta {
        font-size: 0.7rem;
        color: #6c757d;
    }
    .nowrap {
        white-space: nowrap;
    }
</style>
